import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Activity, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Shield,
  BarChart3,
  Lock,
  CreditCard,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { FigIcon } from '../components/FigIcon';

const ResultsPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchScore();
  }, [user]);

  const fetchScore = async () => {
    try {
      const response = await api.get('/score/latest');
      setScore(response.data);
      setPaymentRequired(false);
    } catch (error) {
      if (error.response?.status === 402) {
        // Payment required
        setPaymentRequired(true);
      } else if (error.response?.status === 404) {
        navigate('/questionnaire');
      } else {
        toast.error('Failed to load results');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockResults = async () => {
    setProcessingPayment(true);
    try {
      const response = await api.post('/payments/create-session', {
        package_id: 'results',
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Failed to start checkout');
      setProcessingPayment(false);
    }
  };

  const getRiskColor = (tier) => {
    switch (tier) {
      case 'Low': return '#10B981';
      case 'Moderate': return '#F59E0B';
      case 'High': return '#EF4444';
      default: return '#525252';
    }
  };

  const getRiskBgClass = (tier) => {
    switch (tier) {
      case 'Low': return 'risk-low';
      case 'Moderate': return 'risk-moderate';
      case 'High': return 'risk-high';
      default: return '';
    }
  };

  const getDriverDescription = (driver) => {
    const descriptions = {
      waist: "Elevated waist circumference indicates higher visceral fat, a key driver of insulin resistance.",
      sleep: "Poor sleep quality or duration impairs glucose metabolism and increases cortisol.",
      alcohol: "Regular alcohol consumption stresses the liver and disrupts metabolic function.",
      activity: "Insufficient physical activity reduces insulin sensitivity and muscle glucose uptake.",
      nutrition: "Late eating and ultra-processed foods disrupt circadian rhythm and metabolic health.",
      stress: "Chronic stress elevates cortisol, promoting visceral fat accumulation.",
      family_history: "Genetic predisposition increases baseline risk, making lifestyle factors more critical."
    };
    return descriptions[driver.category] || driver.name;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8E4DC] flex items-center justify-center">
        <div className="text-center">
          <FigIcon className="w-12 h-12 text-[#2F3E30] animate-pulse mx-auto mb-4" />
          <p className="text-[#525252]">Loading your results...</p>
        </div>
      </div>
    );
  }

  // Payment required - show locked state
  if (paymentRequired) {
    return (
      <div className="min-h-screen bg-[#E8E4DC]">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 glass">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <FigIcon className="w-6 h-6 text-[#2F3E30]" />
              <span className="font-bold text-lg" style={{ fontFamily: 'Libre Baskerville, serif' }}>FIG HEALTH</span>
            </Link>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="text-[#525252] hover:text-[#1A1C19]"
            >
              Dashboard
            </Button>
          </div>
        </header>

        {/* Locked Results */}
        <main className="pt-24 pb-12 px-6">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-full bg-[#EBEBE6] border border-[#D1D1CD] flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-[#525252]" />
              </div>
              <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                YOUR RESULTS ARE READY
              </h1>
              <p className="text-[#525252]">
                You've completed the assessment. Unlock your Metabolic Trajectory Score.
              </p>
            </div>

            {/* Teaser */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-8 mb-8">
              <div className="text-center mb-6">
                <div className="relative w-48 h-48 mx-auto mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="96"
                      cy="96"
                      r="80"
                      fill="none"
                      stroke="#D1D1CD"
                      strokeWidth="12"
                    />
                    <circle
                      cx="96"
                      cy="96"
                      r="80"
                      fill="none"
                      stroke="#3f3f46"
                      strokeWidth="12"
                      strokeLinecap="round"
                      strokeDasharray="502"
                      strokeDashoffset="200"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <Lock className="w-8 h-8 text-[#525252] mb-2" />
                    <span className="text-sm text-[#525252]">Score Hidden</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#E8E4DC]">
                  <div className="w-8 h-8 rounded bg-[#D1D1CD]" />
                  <div className="flex-1">
                    <div className="h-4 w-32 bg-[#D1D1CD] rounded" />
                  </div>
                  <div className="h-4 w-8 bg-[#D1D1CD] rounded" />
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#E8E4DC]">
                  <div className="w-8 h-8 rounded bg-[#D1D1CD]" />
                  <div className="flex-1">
                    <div className="h-4 w-40 bg-[#D1D1CD] rounded" />
                  </div>
                  <div className="h-4 w-8 bg-[#D1D1CD] rounded" />
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#E8E4DC]">
                  <div className="w-8 h-8 rounded bg-[#D1D1CD]" />
                  <div className="flex-1">
                    <div className="h-4 w-36 bg-[#D1D1CD] rounded" />
                  </div>
                  <div className="h-4 w-8 bg-[#D1D1CD] rounded" />
                </div>
              </div>

              <p className="text-center text-sm text-[#525252]">
                Your personalized score and top risk drivers are waiting
              </p>
            </div>

            {/* Unlock CTA */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-8 border-[#2F3E30]/30">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                  UNLOCK YOUR RESULTS
                </h3>
                <p className="text-[#525252] mb-6">
                  See your Metabolic Trajectory Score, risk tier, and personalized insights
                </p>
                
                <div className="flex items-baseline justify-center gap-2 mb-6">
                  <span className="text-5xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>£5</span>
                  <span className="text-[#525252]">one-time</span>
                </div>

                <ul className="space-y-2 text-sm mb-6 max-w-xs mx-auto">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#10B981]" />
                    Your Metabolic Trajectory Score (0-100)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#10B981]" />
                    Risk tier assessment
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#10B981]" />
                    Top 5 risk drivers with explanations
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#10B981]" />
                    Component score breakdown
                  </li>
                </ul>

                <Button 
                  onClick={handleUnlockResults}
                  disabled={processingPayment}
                  className="w-full max-w-xs bg-[#2F3E30] hover:bg-[#3A4D3B] text-white h-12 text-lg glow-primary"
                  data-testid="unlock-results-btn"
                >
                  {processingPayment ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Unlock for £5
                    </>
                  )}
                </Button>

                <p className="text-xs text-[#525252] mt-4">
                  Secure payment powered by Stripe
                </p>
              </div>
            </div>

            {/* Upgrade Note */}
            <div className="mt-6 p-4 rounded-lg bg-[#EBEBE6] border border-[#D1D1CD] text-center">
              <p className="text-sm text-[#525252]">
                Want the full 90-day correction plan too?{' '}
                <button 
                  onClick={() => navigate('/pricing')}
                  className="text-[#2F3E30] hover:underline"
                >
                  View full program options
                </button>
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!score) {
    return (
      <div className="min-h-screen bg-[#E8E4DC] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#525252] mb-4">No results found</p>
          <Button onClick={() => navigate('/questionnaire')} className="bg-[#2F3E30] text-white">
            Take Assessment
          </Button>
        </div>
      </div>
    );
  }

  const circumference = 2 * Math.PI * 80;
  const strokeDashoffset = circumference - (score.total_score / 100) * circumference;

  return (
    <div className="min-h-screen bg-[#E8E4DC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <FigIcon className="w-6 h-6 text-[#2F3E30]" />
            <span className="font-bold text-lg" style={{ fontFamily: 'Libre Baskerville, serif' }}>FIG HEALTH</span>
          </Link>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-[#525252] hover:text-[#1A1C19]"
          >
            Dashboard
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Score Display */}
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>
              YOUR METABOLIC FIG HEALTH SCORE
            </h1>
            <p className="text-[#525252]">Based on your responses, here's your current risk profile</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Score Circle */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-8 flex flex-col items-center">
              <div className="relative w-48 h-48 mb-6">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="96"
                    cy="96"
                    r="80"
                    fill="none"
                    stroke="#D1D1CD"
                    strokeWidth="12"
                  />
                  <circle
                    cx="96"
                    cy="96"
                    r="80"
                    fill="none"
                    stroke={getRiskColor(score.risk_tier)}
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    style={{ transition: 'stroke-dashoffset 1s ease-in-out' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-5xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                    {Math.round(score.total_score)}
                  </span>
                  <span className="text-sm text-[#525252]">out of 100</span>
                </div>
              </div>
              <div className={`px-4 py-2 rounded-full text-sm font-medium ${getRiskBgClass(score.risk_tier)}`}>
                {score.risk_tier} Risk Trajectory
              </div>
              <p className="text-center text-[#525252] text-sm mt-4 max-w-xs">
                {score.risk_tier === 'Low' && "Your current trajectory looks favorable. Maintain your habits."}
                {score.risk_tier === 'Moderate' && "There's room for improvement. Targeted changes can shift your trajectory."}
                {score.risk_tier === 'High' && "Your trajectory indicates elevated risk. A focused correction plan is recommended."}
              </p>
            </div>

            {/* Component Scores */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-8">
              <h3 className="font-bold mb-4" style={{ fontFamily: 'Libre Baskerville, serif' }}>SCORE BREAKDOWN</h3>
              <div className="space-y-4">
                {Object.entries(score.component_scores).map(([key, value]) => {
                  const labels = {
                    waist: 'Visceral Risk',
                    sleep: 'Sleep Health',
                    alcohol: 'Alcohol Impact',
                    activity: 'Activity Level',
                    nutrition: 'Nutrition Timing',
                    stress: 'Stress Load',
                    family_history: 'Genetic Risk'
                  };
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-[#525252]">{labels[key]}</span>
                        <span>{Math.round(value)}</span>
                      </div>
                      <div className="h-2 bg-[#D1D1CD] rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${value}%`,
                            backgroundColor: value > 60 ? '#EF4444' : value > 40 ? '#F59E0B' : '#10B981'
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Top Drivers */}
          <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-8 mb-12">
            <h3 className="font-bold mb-6" style={{ fontFamily: 'Libre Baskerville, serif' }}>
              <BarChart3 className="w-5 h-5 inline mr-2 text-[#2F3E30]" />
              YOUR TOP RISK DRIVERS
            </h3>
            <div className="space-y-4">
              {score.drivers.slice(0, 5).map((driver, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-4 p-4 rounded-lg bg-[#E8E4DC] border border-[#D1D1CD]"
                >
                  <div className="w-8 h-8 rounded-full bg-[#D1D1CD] flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{driver.name}</span>
                      <span 
                        className="text-sm font-mono"
                        style={{ color: driver.score > 60 ? '#EF4444' : driver.score > 40 ? '#F59E0B' : '#10B981' }}
                      >
                        {Math.round(driver.score)}
                      </span>
                    </div>
                    <p className="text-sm text-[#525252]">{getDriverDescription(driver)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-8 mb-8 border-[#2F3E30]/30">
            <div className="text-center">
              <TrendingUp className="w-12 h-12 text-[#2F3E30] mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                READY TO CHANGE YOUR FIG HEALTH?
              </h3>
              <p className="text-[#525252] mb-6 max-w-lg mx-auto">
                Get your personalized 90-day correction plan with 4 simple, measurable rules 
                targeting your specific risk drivers.
              </p>
              {user?.is_paid ? (
                <Button 
                  onClick={() => navigate('/plan')}
                  className="bg-[#2F3E30] hover:bg-[#3A4D3B] text-white px-8 py-6 text-lg glow-primary"
                  data-testid="view-plan-btn"
                >
                  View My Plan
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={() => navigate('/pricing')}
                  className="bg-[#2F3E30] hover:bg-[#3A4D3B] text-white px-8 py-6 text-lg glow-primary"
                  data-testid="unlock-plan-btn"
                >
                  Unlock My Plan
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              )}
            </div>
          </div>

          {/* Medical Disclaimer */}
          <div className="p-6 rounded-xl bg-[#EBEBE6] border border-[#D1D1CD]">
            <div className="flex items-start gap-4">
              <Shield className="w-6 h-6 text-[#525252] flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>IMPORTANT DISCLAIMER</h4>
                <p className="text-[#525252] text-sm leading-relaxed">
                  This score represents an educational estimate of metabolic risk trajectory based on 
                  self-reported data. <strong className="text-[#1A1C19]">This is not a medical diagnosis, 
                  treatment, or medical advice.</strong> The algorithm uses published research but 
                  cannot account for individual medical conditions. If you have concerning symptoms, 
                  elevated values, or existing health conditions, consult a qualified healthcare provider.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResultsPage;
